import React, { SyntheticEvent, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Snackbar,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { ContentAreaDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import { getStyles } from "../styles";
import MuiAlert from '@mui/material/Alert';
import managementService from "../../../services/AdminManagementService";

interface ContentAreaManagementProps {
  site: SiteDTO;
  showMessage: (msg: string, severity: "error" | "info" | "success" | "warning") => void;
  closeMessage: () => void;
  messageSettings: {
    open: boolean;
    message: string;
    severity: 'error' | 'info' | 'success' | 'warning';
  };
}

export const ContentAreaManagement = ({
  site,
  showMessage,
  closeMessage,
  messageSettings,
}: ContentAreaManagementProps) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [availableContentAreas, setAvailableContentAreas] = useState<ContentAreaDTO[]>([]);
  const [selectedContentAreas, setSelectedContentAreas] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const getContentAreasForSite = async () => {
      const contentAreas = await managementService.getContentAreasForSite(site.id);
      if (contentAreas?.length) {
        setAvailableContentAreas(contentAreas);
        setSelectedContentAreas(contentAreas.filter(area => area.isSelected).map(area => area.code));
      }
      setIsLoading(false);
    };
    getContentAreasForSite();
  }, [site.id]);

  const handleContentAreaSelections = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    if (event.target.checked) {
      setSelectedContentAreas([...selectedContentAreas, selectedValue]);
    }
    if (!event.target.checked) {
      setSelectedContentAreas(selectedContentAreas.filter(x => x !== selectedValue));
    }
  }

  const saveContentAreaSelections = async () => {
    const result = await managementService.setSiteContentAreas(site.id, selectedContentAreas);

    if (result.isErr()) {
      showMessage("Could not save content areas...", "error");
    }
    else {
      showMessage("Saved content areas successfully!", "success");
    }
  };


  if (isLoading) {
    return <Box sx={[styles.loaderContainer, styles.indentedFields]}>
      <CircularProgress disableShrink />
    </Box>
  }
  if (!isLoading && !availableContentAreas.length) {
    return (
      <Box sx={{ margin: '16px 10px' }}>
        <Typography>
          No content areas available for site...
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ margin: '16px 10px' }}>
      <Typography>
        Content areas to include in Educator Academy
      </Typography>
      <FormGroup sx={styles.indentedFields}>
        {availableContentAreas.map(area => {
          return <FormControlLabel
            key={area.code}
            label={area.name}
            control={
              <Checkbox
                color='primary'
                defaultChecked={area.isSelected}
                value={area.code}
                onChange={handleContentAreaSelections}
                data-testid={`checkbox-${area.code}`}
              />
            }
          />
        })}
      </FormGroup>
      <Box sx={styles.rightAlign}>
        <Button
          variant="contained"
          color="primary"
          onClick={saveContentAreaSelections}
        >
          Save
        </Button>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={messageSettings.open}
        autoHideDuration={5000}
        onClose={closeMessage}
        sx={{ position: 'absolute', bottom: '0px' }}
      >
        <MuiAlert
          onClose={closeMessage}
          severity={messageSettings.severity}
          elevation={6}
          variant="filled"
        >
          {messageSettings.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};